/**
 * TechnologyPage — dedicated /technology route.
 *
 * Replaces the prior AnchorRedirect → /about#pillars. The route now
 * ships its own crawlable HTML with route-specific SEO so the search
 * footprint covers "NoJo Enterprise technology" queries.
 *
 * Content discipline:
 *   - Reuses the canonical three-pillar copy from AboutPage (mobility,
 *     records, digital infrastructure). No invented claims.
 *   - Stack disclosure is factual: FastAPI + React + MongoDB on JST
 *     ops, AI-supported workflows. These facts already appear in PRD.md
 *     and operator docs.
 */
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Cpu,
  Database,
  FileText,
  Languages,
  Map,
  ServerCog,
  Shield,
  Sparkles,
} from "lucide-react";

import Breadcrumb from "@/components/Breadcrumb";
import Reveal from "@/components/motion/Reveal";
import SEO, { PAGE_SEO } from "@/components/SEO";
import PageSideAura from "@/components/motion/PageSideAura";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

const PILLARS = [
  {
    key: "mobility",
    accent: "#5B2E83",
    Icon: Map,
    labelEn: "Mobility",
    labelJa: "モビリティ",
    detailEn:
      "Making ETC and Japan's expressway infrastructure easier to access for everyone — JapanETCcard.com is the customer-facing entry point.",
    detailJa:
      "ETC・道路インフラを誰もが使いやすく。JapanETCcard.comがその窓口です。",
  },
  {
    key: "records",
    accent: "#2F8F3C",
    Icon: FileText,
    labelEn: "Records",
    labelJa: "記録",
    detailEn:
      "Organizing usage records and producing clear reports for daily operations — JapanTollReceipts.jp delivers daily / weekly / monthly toll reports without manual MEISAI lookups.",
    detailJa:
      "利用記録の整理・レポートで業務を支援。JapanTollReceipts.jpが日次・週次・月次の利用明細・利用証明書を自動配信します。",
  },
  {
    key: "digital",
    accent: "#0E5FB0",
    Icon: Sparkles,
    labelEn: "Digital Infrastructure",
    labelJa: "デジタル基盤",
    detailEn:
      "Payment, data organization and multilingual access — connected into long-lasting infrastructure that every NoJo Enterprise service builds on.",
    detailJa:
      "決済・データ整理・多言語対応を、長く使える基盤に。すべてのサービスがこの基盤の上で動きます。",
  },
];

const STACK = [
  {
    Icon: ServerCog,
    accent: "#0E5FB0",
    labelEn: "FastAPI on Python (JST ops)",
    labelJa: "FastAPI on Python（JST運用）",
    bodyEn:
      "Every backend service runs FastAPI on JST time, with audit-log-ready mutations and JWT-ready auth scaffolding.",
    bodyJa:
      "すべてのバックエンドはFastAPIで構築。JSTタイムスタンプ、監査ログ対応の変更処理、JWT認証基盤を備えています。",
  },
  {
    Icon: Database,
    accent: "#2F8F3C",
    labelEn: "MongoDB",
    labelJa: "MongoDB",
    bodyEn:
      "Document store with composite-key upserts and stale-resistant federation caching. Customer data is partitioned per service.",
    bodyJa:
      "複合キーのアップサートと耐久性のあるフェデレーションキャッシュ。お客様データはサービスごとに分離して保管します。",
  },
  {
    Icon: Cpu,
    accent: "#5B2E83",
    labelEn: "React + Tailwind (bilingual)",
    labelJa: "React + Tailwind（日英対応）",
    bodyEn:
      "Every public surface ships in Japanese (primary) and English. Static delivery via CDN with social-crawler edge rendering.",
    bodyJa:
      "公開ページはすべて日本語（主言語）と英語の両対応。CDN配信およびソーシャルクローラー向けのエッジレンダリングを実装。",
  },
  {
    Icon: Languages,
    accent: "#0F1D2D",
    labelEn: "AI Operations team",
    labelJa: "AI支援チーム",
    bodyEn:
      "Five purpose-trained AI workflows support customer-care, ops automation, content QA, code review and incident response across the three services.",
    bodyJa:
      "目的別に訓練された5つのAIワークフローが、お客様対応・運用自動化・コンテンツ品質・コードレビュー・インシデント対応を支えます。",
  },
];

export default function TechnologyPage({ lang }) {
  const isJa = lang === "ja";

  return (
    <article data-testid="technology-page" className="bg-white">
      <SEO {...PAGE_SEO.technology[lang]} path={`/${lang}/technology`} lang={lang} />
      <JsonLd
        data={[
          breadcrumbSchema([
            { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
            { name: isJa ? "テクノロジー" : "Technology", url: `/${lang}/technology` },
          ]),
        ]}
      />
      <Breadcrumb lang={lang} crumbs={[{ label: isJa ? "テクノロジー" : "Technology" }]} />

      {/* HERO */}
      <section className="relative isolate bg-white overflow-hidden">
        <PageSideAura testId="technology-hero-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-14 pb-10 md:pb-14">
          <Reveal as="div" variant="up">
            <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] font-bold uppercase">
              {isJa ? "テクノロジー" : "Technology"}
            </div>
            <h1
              className={[
                "mt-3",
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] tracking-tighter font-bold leading-[1.08] max-w-3xl",
              ].join(" ")}
              data-testid="technology-hero-title"
            >
              {isJa
                ? "三つの柱の上に、長く続く運用を載せる。"
                : "Three pillars, one operating discipline."}
            </h1>
            <p className="mt-5 text-[15px] md:text-[16px] leading-[1.85] text-[#475569] max-w-2xl">
              {isJa
                ? "NoJo Enterpriseは、モビリティ・記録・デジタル基盤の3領域を、同じ運用ディシプリンで支えています。派手さよりも、長く続く仕組みと、現場で本当に役立つ運用を選びます。"
                : "NoJo Enterprise supports three domains — mobility, records and digital infrastructure — with one shared operating discipline. We choose lasting infrastructure and real operational utility over short-term flash."}
            </p>
          </Reveal>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section
        id="pillars"
        data-testid="technology-pillars"
        className="bg-[#F8F9FA] border-y border-[#E2E8F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] uppercase font-bold mb-3">
            {isJa ? "3つの柱" : "Three Pillars"}
          </div>
          <h2
            className={[
              "mb-10",
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] max-w-2xl",
            ].join(" ")}
          >
            {isJa
              ? "モビリティ・記録・デジタル基盤の3領域で社会を支える。"
              : "Three pillars supporting Japan: mobility, records, digital infrastructure."}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#E2E8F0] border-y border-[#E2E8F0]">
            {PILLARS.map((p, i) => (
              <Reveal
                key={p.key}
                variant="scale"
                delay={i * 120}
                data-testid={`technology-pillar-${p.key}`}
                className="py-8 md:py-10 md:px-8 first:md:pl-0 last:md:pr-0"
              >
                <span
                  aria-hidden
                  className="inline-flex items-center justify-center h-12 w-12 animate-brand-float"
                  style={{ backgroundColor: `${p.accent}15`, animationDelay: `${i * 250}ms` }}
                >
                  <p.Icon className="h-6 w-6" strokeWidth={1.5} style={{ color: p.accent }} />
                </span>
                <div
                  className="mt-5 text-[10px] tracking-[0.26em] font-mono-display uppercase font-bold"
                  style={{ color: p.accent }}
                >
                  {`0${i + 1} · ${isJa ? p.labelJa : p.labelEn}`}
                </div>
                <h3
                  className={[
                    "mt-2",
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-[22px] font-bold tracking-tight leading-snug",
                  ].join(" ")}
                >
                  {isJa ? p.labelJa : p.labelEn}
                </h3>
                <p className="mt-3 text-[13.5px] leading-[1.8] text-[#475569] max-w-xs">
                  {isJa ? p.detailJa : p.detailEn}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STACK / OPERATING FOUNDATION */}
      <section data-testid="technology-stack" className="relative isolate bg-white">
        <PageSideAura testId="technology-stack-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] uppercase font-bold mb-3">
            {isJa ? "技術基盤" : "Operating foundation"}
          </div>
          <h2
            className={[
              "mb-10",
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] max-w-2xl",
            ].join(" ")}
          >
            {isJa
              ? "三つのサービスを、同じ基盤の上で運用しています。"
              : "Three services run on one shared operating foundation."}
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {STACK.map((s, i) => (
              <Reveal
                as="li"
                key={s.labelEn}
                variant="up"
                delay={i * 90}
                data-testid={`technology-stack-${i}`}
                className="bg-white rounded-2xl border border-[#E2E8F0] p-5 md:p-6 hover:-translate-y-0.5 transition-transform"
              >
                <span
                  aria-hidden
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md"
                  style={{ backgroundColor: `${s.accent}15` }}
                >
                  <s.Icon className="h-5 w-5" strokeWidth={1.7} style={{ color: s.accent }} />
                </span>
                <h3
                  className={[
                    "mt-3",
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-[17px] font-bold tracking-tight leading-snug",
                  ].join(" ")}
                >
                  {isJa ? s.labelJa : s.labelEn}
                </h3>
                <p className="mt-2 text-[13.5px] leading-[1.85] text-[#475569]">
                  {isJa ? s.bodyJa : s.bodyEn}
                </p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="technology-cta" className="relative isolate overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 35%, #2563EB 65%, #1D4ED8 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-25"
          style={{
            background:
              "radial-gradient(900px 320px at 20% 0%, rgba(255,255,255,0.22), transparent 60%), radial-gradient(700px 280px at 80% 100%, rgba(255,255,255,0.14), transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="text-[10px] tracking-[0.28em] font-mono-display text-white/85 uppercase font-bold">
                {isJa ? "詳細を見る" : "Explore further"}
              </div>
              <h2
                className={[
                  "mt-3",
                  isJa ? "font-ja" : "font-display",
                  "text-white text-2xl md:text-3xl font-bold tracking-tight leading-[1.2]",
                ].join(" ")}
              >
                {isJa
                  ? "私たちの運用ディシプリンと、信頼へのお約束。"
                  : "Our operating discipline, and our trust commitments."}
              </h2>
              <p className="mt-4 text-[14.5px] leading-[1.85] text-white/90 max-w-2xl">
                {isJa
                  ? "信頼とセキュリティに関する取り組みは、別ページにまとめています。"
                  : "Our trust and security commitments are documented on a separate page."}
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
              <Link
                to={`/${lang}/trust-security`}
                data-testid="technology-cta-trust"
                className="inline-flex items-center gap-2 bg-white text-[#1D4ED8] px-5 h-12 rounded-full text-[13px] font-semibold tracking-tight hover:bg-[#EFF6FF] transition-colors"
              >
                <Shield className="h-4 w-4" strokeWidth={2.25} />
                {isJa ? "信頼とセキュリティ" : "Trust & Security"}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Link>
              <Link
                to={`/${lang}/services`}
                data-testid="technology-cta-services"
                className="inline-flex items-center gap-2 ring-1 ring-white/50 text-white px-5 h-12 rounded-full text-[13px] font-semibold tracking-tight hover:bg-white/15 transition-colors"
              >
                {isJa ? "運営サービス" : "Our services"}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
